import { CalendarOptions, EventContentArg, ViewContentArg } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import { format, isSameDay } from 'date-fns';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { LuChevronLeft, LuChevronRight } from 'react-icons/lu';
import { useSearchParams } from 'react-router-dom';

import { EventView } from '@ndvr/customer-api';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { PageTitle } from '@/components/ui/page-title';
import { convertBackendTimeToBrowser, formatTime } from 'utils';
import { cn } from 'utils/cn';
import { EventDetailsDrawer } from 'views/household/HouseholdEvents/EventDetailsDrawer';

import { CALENDAR_EVENT_TYPE_CARD_CLASSES, getCalendarEventType, matchesEventTypeFilter } from '../calendar-event-type';
import { useCalendarViews, useHasConnectedCalendar } from '../calendar-queries';
import { formatRangeTitle } from '../calendar-range';
import { EventCalendarView, useEventCalendarViewState } from '../calendar-state';
import './calendar-event.css';
import './day-grid-month.css';
import { EventCalendarActionMenu } from './EventCalendarActionMenu';
import EventCalendarDetailsSheet from './EventCalendarDetailsSheet';
import { EventCalendarTypeFilter } from './EventCalendarTypeFilter';
import './time-grid-day.css';
import './time-grid-week.css';

function formatCalendarHeader(viewContent: ViewContentArg | undefined, calendarView: EventCalendarView): string {
    if (!viewContent) return '';

    // Month view's activeStart includes leading days from the previous month, so use
    // currentStart there; activeEnd is exclusive, hence the -1ms.
    const start = new Date(
        calendarView === EventCalendarView.DAY_GRID_MONTH
            ? viewContent.view.currentStart
            : viewContent.view.activeStart,
    );
    const end = new Date(new Date(viewContent.view.activeEnd).getTime() - 1);
    return formatRangeTitle(start, end, calendarView);
}

function formatCalendarEventSecondaryText(start: Date | null, end: Date | null): string {
    if (!start || !end) return '';

    if (isSameDay(start, end)) {
        return `${formatTime(start)} - ${formatTime(end)}`;
    }

    return `${format(start, 'MMM d')} - ${format(end, 'MMM d')}`;
}

function renderCalendarEventContent(content: EventContentArg) {
    const eventView = content.event.extendedProps as EventView | undefined;
    const start = content.event.start;
    const end = content.event.end;
    const eventType = eventView ? getCalendarEventType(eventView) : 'internal';
    const eventCardClassName = CALENDAR_EVENT_TYPE_CARD_CLASSES[eventType];
    const secondaryText = formatCalendarEventSecondaryText(start, end);
    const isMonthView = content.view.type === 'dayGridMonth';

    return (
        <div
            className={cn(
                'flex h-full w-full items-center overflow-hidden rounded-md border text-left shadow-none',
                eventCardClassName,
                isMonthView ? 'px-1 py-1' : 'px-1 py-1',
            )}
        >
            <div className="min-w-0 space-y-px">
                <div className="font-halyard text-cocoa-800 truncate text-[11px] leading-[1.2] font-medium tracking-[-0.02em]">
                    {eventView?.title ?? content.event.title}
                </div>
                {secondaryText ? (
                    <div className="font-halyard text-muted-foreground truncate text-[10px] leading-[1.2] font-normal tracking-[-0.02em]">
                        {secondaryText}
                    </div>
                ) : null}
            </div>
        </div>
    );
}

const viewConfigs: Record<EventCalendarView, CalendarOptions> = {
    [EventCalendarView.DAY_GRID_MONTH]: {
        dayMaxEventRows: 4,
        eventContent: renderCalendarEventContent,
    },
    [EventCalendarView.TIME_GRID_WEEK]: {
        eventContent: renderCalendarEventContent,
        slotDuration: '00:30:00',
        scrollTime: '09:00:00',
    },
    [EventCalendarView.TIME_GRID_DAY]: {
        eventContent: renderCalendarEventContent,
        slotDuration: '00:30:00',
        scrollTime: '09:00:00',
    },
};

// Passing anchorDate puts the calendar in controlled mode (CC_CALENDAR_NOTES): the shared
// page header owns navigation, so the internal header is hidden and FullCalendar follows the
// prop. Without it the calendar renders and navigates exactly as before the flag existed.
export const EventCalendar = ({ actions, anchorDate }: { actions?: ReactNode; anchorDate?: Date }) => {
    const calendarRef = useRef<FullCalendar>(null);
    const hasConnectedCalendar = useHasConnectedCalendar();
    const calendarApi = calendarRef.current?.getApi();
    const { eventCalendarViewState } = useEventCalendarViewState();
    const containerRef = useRef<HTMLDivElement>(null);
    const isControlled = anchorDate !== undefined;

    useEffect(() => {
        if (anchorDate) calendarRef.current?.getApi().gotoDate(anchorDate);
    }, [anchorDate]);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        let rafId = 0;
        const observer = new ResizeObserver(() => {
            cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                calendarRef.current?.getApi().updateSize();
            });
        });
        observer.observe(el);
        return () => {
            cancelAnimationFrame(rafId);
            observer.disconnect();
        };
    }, []);

    const [viewContent, setViewContent] = useState<ViewContentArg>();
    const [sheetDate, setSheetDate] = useState<Date | null>(null);
    const [selectedEvents, setSelectedEvents] = useState<EventView[]>([]);
    const [searchParams, setSearchParams] = useSearchParams();

    // Get calendar data for the current view
    const fromDate = viewContent?.view.activeStart;
    const toDate = viewContent?.view.activeEnd;
    const calendarViews = useCalendarViews({ fromDate, toDate });

    // The open event details drawer is driven by the eventGUID search param so the URL is
    // shareable/restorable; the event must be within the loaded view range to resolve.
    const openEventGuid = searchParams.get('eventGUID');
    const openEvent = openEventGuid ? (calendarViews.data?.eventViewMap.get(openEventGuid) ?? null) : null;

    const setOpenEventGuid = (eventGuid: string | null) => {
        setSearchParams(
            (params) => {
                if (eventGuid) {
                    params.set('eventGUID', eventGuid);
                } else {
                    params.delete('eventGUID');
                }
                return params;
            },
            { replace: true },
        );
    };

    const eventTypeFilter = eventCalendarViewState.eventTypeFilter;

    const filteredCalendarEvents = useMemo(() => {
        const events = calendarViews.data?.calendarEvents ?? [];
        return events.filter((event) => matchesEventTypeFilter(event, eventTypeFilter));
    }, [calendarViews.data?.calendarEvents, eventTypeFilter]);

    const filteredFullCalendarEvents = useMemo(() => {
        const events = calendarViews.data?.fullCalendarEvents ?? [];
        // Without an active filter, events missing from eventViewMap must still render.
        if (!eventTypeFilter) return events;
        return events.filter((event) => {
            const eventView = calendarViews.data?.eventViewMap.get(event.id);
            return eventView && matchesEventTypeFilter(eventView, eventTypeFilter);
        });
    }, [calendarViews.data, eventTypeFilter]);

    const eventsOverlappingDay = useMemo(
        () => (day: Date) => {
            const dayStart = new Date(day);
            dayStart.setHours(0, 0, 0, 0);
            const dayEnd = new Date(day);
            dayEnd.setHours(23, 59, 59, 999);

            return filteredCalendarEvents
                .filter((ev) => {
                    const start = convertBackendTimeToBrowser(ev.startTime, ev.startTimeZone);
                    const end = convertBackendTimeToBrowser(ev.endTime, ev.endTimeZone);
                    return start <= dayEnd && end >= dayStart;
                })
                .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
        },
        [filteredCalendarEvents],
    );

    if (hasConnectedCalendar.data === false || hasConnectedCalendar.isPending) return null;

    return (
        <div ref={containerRef} className={cn('flex flex-col gap-6', isControlled ? 'min-h-0 flex-1' : 'h-full')}>
            {!isControlled && (
                <div className="flex w-full items-center justify-between gap-8 py-4">
                    <div className="flex items-center gap-4">
                        <div className="border-cocoa-100 flex items-center gap-2 rounded-full border bg-white p-1">
                            <Button
                                variant="ghost2"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={() => calendarApi?.prev()}
                            >
                                <LuChevronLeft />
                            </Button>
                            <Button
                                variant="ghost2"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={() => calendarApi?.next()}
                            >
                                <LuChevronRight />
                            </Button>
                        </div>
                        <div className="min-w-[240px]">
                            <PageTitle>
                                {formatCalendarHeader(viewContent, eventCalendarViewState.calendarView)}
                            </PageTitle>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <EventCalendarTypeFilter />
                        <EventCalendarActionMenu />
                        {actions}
                    </div>
                </div>
            )}
            <Card className="flex flex-1 flex-col border-0 bg-white p-0 shadow-none">
                <CardContent
                    className={cn('flex flex-1 flex-col p-0', {
                        'day-grid-month': eventCalendarViewState.calendarView === EventCalendarView.DAY_GRID_MONTH,
                        'time-grid-week': eventCalendarViewState.calendarView === EventCalendarView.TIME_GRID_WEEK,
                        'time-grid-day': eventCalendarViewState.calendarView === EventCalendarView.TIME_GRID_DAY,
                    })}
                >
                    <FullCalendar
                        ref={calendarRef}
                        key={eventCalendarViewState.calendarView}
                        initialView={eventCalendarViewState.calendarView}
                        initialDate={anchorDate}
                        plugins={[dayGridPlugin, interactionPlugin, timeGridPlugin]}
                        height="100%"
                        eventDataTransform={(event) => {
                            if (event.id) {
                                event.extendedProps = calendarViews.data?.eventViewMap?.get(event.id);
                            }
                            return event;
                        }}
                        weekends={eventCalendarViewState.showWeekends}
                        events={filteredFullCalendarEvents}
                        selectable
                        dateClick={(info) => {
                            setOpenEventGuid(null);
                            setSheetDate(info.date);
                            setSelectedEvents(eventsOverlappingDay(info.date));
                        }}
                        eventClick={(info) => {
                            setSheetDate(null);
                            setSelectedEvents([]);
                            // FullCalendar event ids are eventGUIDs (see mapToFullCalendarEvent)
                            setOpenEventGuid(info.event.id);
                        }}
                        datesSet={(info) => {
                            setViewContent(info);
                        }}
                        viewDidMount={(info) => {
                            setViewContent(info);
                        }}
                        eventClassNames={['calendar-event']}
                        {...viewConfigs[eventCalendarViewState.calendarView]}
                    />
                </CardContent>
            </Card>

            <EventCalendarDetailsSheet
                date={sheetDate}
                events={selectedEvents}
                open={!!sheetDate}
                onOpenChange={(open) => {
                    if (!open) {
                        setSheetDate(null);
                        setSelectedEvents([]);
                    }
                }}
                onOpenEventDetails={(event) => {
                    // Close the day sheet and open the EventDetailsDrawer for this event
                    setSheetDate(null);
                    setSelectedEvents([]);
                    setOpenEventGuid(event.eventGUID);
                }}
            />
            <EventDetailsDrawer
                event={openEvent}
                isOpen={!sheetDate && !!openEvent}
                onOpenChange={(open) => {
                    if (!open) {
                        setOpenEventGuid(null);
                    }
                }}
            />
        </div>
    );
};
